/**
 * Account lifecycle + profile sections — everything under `/users/me/*` that isn't the rider-form
 * defaults (those stay in `users-api.ts`), plus the unauthenticated recovery endpoints under
 * `/auth/*`.
 *
 * One module per resource area, per CLAUDE.md working rule #8.
 */
import { authFetch, parseOrThrow, storeRefreshToken } from "./http";
import { sharedGet } from "./request-share";

export interface UserPersonal {
  displayName: string;
  personalPhone: string;
  country: string;
  city: string;
  locale: string;
}

export interface UserWork {
  company: string;
  userRole: string;
  /**
   * The rental's warehouse address (SCRUM-851); "" = none. Absent on an older API. On a WRITE, left out means «do not touch»:
   * the role tile and sign-up write company and role only, and must not erase it.
   */
  warehouseAddress?: string;
}

export interface UserAccountState {
  id: string;
  email: string;
  emailVerified: boolean;
  passwordChangedAt: string | null;
  createdAt: string;
  termsAcceptedAt: string | null;
  marketingConsent: boolean;
  deletionRequestedAt: string | null;
  deletionScheduledFor: string | null;
  /** The not-yet-confirmed new address from an in-flight email change, or `null`. */
  pendingEmail: string | null;
  /** `users.locale`, or `null` if never set. Same column `updatePersonal`'s `locale` field and
   *  `updateLocale` below both write. */
  locale: string | null;
}

/** The roles the API accepts. Mirrors `USER_ROLES` in `apps/api/src/users/users.service.ts` —
 *  anything else is coerced to "" server-side, so keep the two lists in step. Display labels are
 *  translatable, so they live in `pages/profile-page.ts#ROLE_LABEL_KEYS` (the i18n dictionary),
 *  not here — this module carries only the wire `value`s. */
export const USER_ROLES = ["soundman", "manager", "rental", "other"] as const;

async function getJson<T>(path: string): Promise<T> {
  const res = await authFetch(path);
  return parseOrThrow<T>(res);
}

async function sendJson<T>(path: string, method: "PUT" | "POST", body: unknown): Promise<T> {
  const res = await authFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  return parseOrThrow<T>(res);
}

// --- Profile sections ---------------------------------------------------------------------------

export const getPersonal = (): Promise<UserPersonal> => getJson("/users/me/personal");
export const updatePersonal = (p: UserPersonal): Promise<UserPersonal> => sendJson("/users/me/personal", "PUT", p);

export const getWork = (): Promise<UserWork> => getJson("/users/me/work");
export const updateWork = (w: UserWork): Promise<UserWork> => sendJson("/users/me/work", "PUT", w);

/** Склеено на всплеск (SCRUM-887): состояние аккаунта независимо спрашивают гейт согласия, баннер
 *  про почту и профиль — на одной загрузке это было два-четыре одинаковых запроса. */
export const getAccountState = (): Promise<UserAccountState> =>
  sharedGet("GET /users/me/account", () => getJson<UserAccountState>("/users/me/account"));

/** Records agreement to the CURRENT terms/privacy documents (components/consent-gate.ts). */
export const acceptTerms = (): Promise<{ ok: true }> => sendJson("/users/me/consents/terms", "POST", {});

/** Persists ONLY the locale column — called by `i18n/index.ts#setLocale` for a logged-in user, so
 *  switching language in the side-nav switcher is remembered on the account, not just this browser
 *  (docs/V2_I18N_PLAN.md phase 2). Deliberately separate from `updatePersonal` (which would need
 *  the rest of the personal section loaded first just to round-trip it unchanged). */
export const updateAccountLocale = (locale: string): Promise<{ locale: string }> =>
  sendJson("/users/me/locale", "POST", { locale });

// --- Security -----------------------------------------------------------------------------------

/** A password change revokes every refresh token of the account; the reply carries a fresh one for
 *  THIS device, which is stored here so the person is not signed out 15 minutes later (SCRUM-729). */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ ok: true }> => {
  const res = await sendJson<{ ok: true; refreshToken?: string }>("/auth/password/change", "POST", { currentPassword, newPassword });
  if (res.refreshToken) storeRefreshToken(res.refreshToken);
  return { ok: true };
};

export const requestEmailVerification = (email: string): Promise<{ ok: true }> =>
  sendJson("/auth/verify-email/request", "POST", { email });

/** The first password of an account made by an email link (SCRUM-591); signed in, once. */
export const setInitialPassword = (password: string): Promise<{ ok: true }> =>
  sendJson("/auth/password/set", "POST", { password });

export const confirmEmailVerification = (token: string): Promise<{ verified: boolean }> =>
  sendJson("/auth/verify-email/confirm", "POST", { token });

export const requestPasswordReset = (email: string): Promise<{ ok: true }> =>
  sendJson("/auth/password-reset/request", "POST", { email });

export const confirmPasswordReset = (token: string, password: string): Promise<{ ok: true }> =>
  sendJson("/auth/password-reset/confirm", "POST", { token, password });

// --- Email change (double opt-in) ----------------------------------------------------------------

export const requestEmailChange = (newEmail: string, password: string): Promise<{ ok: true }> =>
  sendJson("/auth/email-change", "POST", { newEmail, password });

export const confirmEmailChange = (token: string): Promise<{ verified: boolean }> =>
  sendJson("/auth/email-change/confirm", "POST", { token });

// --- Consents + GDPR ------------------------------------------------------------------------------

export const setMarketingConsent = (granted: boolean): Promise<{ marketingConsent: boolean }> =>
  sendJson("/users/me/consents/marketing", "POST", { granted });

export const requestAccountDeletion = (): Promise<UserAccountState> =>
  sendJson("/users/me/deletion/request", "POST", {});

export const cancelAccountDeletion = (): Promise<UserAccountState> =>
  sendJson("/users/me/deletion/cancel", "POST", {});

/**
 * Fetches the caller's full data export and hands back a Blob URL for download. Deliberately not
 * a plain link to the endpoint: the route needs an `Authorization` header, which a bare `<a href>`
 * navigation cannot carry.
 */
export async function downloadMyData(): Promise<{ url: string; filename: string }> {
  const data = await getJson<Record<string, unknown>>("/users/me/export");
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const stamp = new Date().toISOString().slice(0, 10);
  return { url: URL.createObjectURL(blob), filename: `soundcheckbox-my-data-${stamp}.json` };
}
